/**
 * PageHeader.jsx
 *
 * #1804 -- real, Stinger direct 2026-08-24. HUNT (HuntLauncher.jsx) and
 * Tech Sheets (TechSheets.jsx, whose own code comment already says
 * "Gold Standard header, matches HuntLauncher.jsx exactly") are the
 * locked reference. Extracted here as one real shared component instead
 * of 9 separate hand-copies -- copying markup 9 times is exactly how
 * two pages drift out of sync with each other, which is the same real
 * problem #1804 was filed to fix in the first place.
 *
 * REAL, FLAGGED DISCREPANCY: the ticket describes "a small icon badge
 * (dark rounded square, ~40px)". Neither actual reference page does
 * this -- both use a plain 36×36 image with no background/badge
 * wrapper at all, confirmed by reading both files directly. Built to
 * match the real, already-shipped reference exactly rather than a
 * verbal description that doesn't match what's actually live -- this
 * is what "match HUNT and Tech Sheets" as the stated goal actually
 * requires.
 *
 * Props:
 *   icon        - image src (reuses the existing sidebar-nav PNG for
 *                 every page in #1804's scope -- confirmed all 9 exist,
 *                 no new Iceman commissions needed for this pass)
 *   title       - real page name; rendered as-is, so pass already-
 *                 uppercased text (matches both reference files, which
 *                 hardcode "HUNT" / "TECH SHEETS" rather than calling
 *                 .toUpperCase() on a prop)
 *   tagline     - short pill text (e.g. "The Toolbox")
 *   description - muted description line below
 *   color       - pill background color; each page already computes
 *                 its own real color (partner brand color or a fixed
 *                 module accent) -- passed through unchanged
 */

import React from 'react';

const TITLE_ACRONYMS = new Set(['CRM', 'REPD', 'API', 'AI']);
function sentenceCaseTitle(value) {
  const text = String(value || '');
  // Legacy page callers pass all-caps labels; normalize those while retaining
  // common product/department acronyms. Mixed-case names are already authored.
  if (text !== text.toLocaleUpperCase()) return text;
  return text.toLocaleLowerCase().replace(/\b[\p{L}]+\b/gu, (word) => {
    const upper = word.toLocaleUpperCase();
    return TITLE_ACRONYMS.has(upper) ? upper : word.charAt(0).toLocaleUpperCase() + word.slice(1);
  });
}

export default function PageHeader({ title, description, icon, tagline, color }) {
  const sentenceTitle = sentenceCaseTitle(title);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        {icon && <img src={icon} alt="" style={{ width: 36, height: 36, objectFit: 'contain', marginRight: 10 }} />}
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: 0, color: 'var(--gov-text-primary)' }}>{sentenceTitle}</h1>
        {tagline && <span style={{ marginLeft: 10, padding: '4px 9px', borderRadius: 6, background: color || 'var(--gov-surface-muted)', color: 'var(--gov-text-primary)', fontSize: 12 }}>{tagline}</span>}
      </div>
      {description && (
        <div title={description} style={{ fontSize: 13, color: 'var(--gov-text-muted, #64748b)', lineHeight: 1.4, marginBottom: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {description}
        </div>
      )}
    </>
  );
}
